/* eslint-disable camelcase */
import { DEFAULT_ENTROPY } from 'pages/Remixer/constants';
import { AIGeneratedSong, SongType } from 'services/api/entities';
import {
  BaseSongResponse,
  MainSongResponse,
  RemixerProjectsListResponse,
  RemixerProjectResponse,
  FormulaExtraParamsResponse,
} from 'services/api/responses/responseTypes';
import { DEFAULT_EFFECTS_ORDER } from 'services/store/remixer/constants';
import type { HistorySong, RemixerProject } from 'services/store/remixer/types';

const transformUserLoop = (data?: FormulaExtraParamsResponse | null) => {
  if (!data) {
    return null;
  }

  const { user_audio: userAudio } = data;
  if (!userAudio) {
    return null;
  }

  return {
    loopId: userAudio.id,
    loopFileName: userAudio.filename,
    entropy: data.entropy ?? DEFAULT_ENTROPY,
    instrument: '',
  };
};

const transformSong = (
  {
    music_file_path: musicFilePath,
    is_in_library: isInLibrary,
    wav_file_path: wavFilePath,
    ...song
  }: MainSongResponse | BaseSongResponse,
  songType: SongType
): AIGeneratedSong => ({
  ...song,
  songType,
  isInLibrary,
  musicFilePath,
  wavFilePath,
  userLoop: transformUserLoop(song.formula?.extra_params),
});

export const transformSongResponse = (data: BaseSongResponse) => ({
  data: transformSong(data, SongType.AI_GENERATED),
});

export interface TranformedRemixerProjectResponse extends RemixerProject {
  id: NonNullable<RemixerProject['id']>;
  historySongs: Array<HistorySong>;
}

export const transformProjectResponse = ({
  music_file_path,
  created_at,
  is_in_library,
  last_saved_at,
  history,
  history_pointer,
  history_songs,
  vega_song,
  song,
  ...data
}: RemixerProjectResponse): TranformedRemixerProjectResponse => ({
  ...data,
  musicFilePath: music_file_path,
  createdAt: created_at,
  isInLibrary: is_in_library,
  lastSavedAt: last_saved_at,
  history: history.map(({ formula, parentSongId }) => ({
    formula,
    songRequestId: parentSongId,
  })),
  historyPointer: history_pointer,
  historySongs: history_songs.map((historySong) => ({
    id: historySong.song_id,
    musicFilePath: historySong.music_file_path,
    duration: historySong.duration,
  })),
  ...(song
    ? {
        song: transformSong(song, SongType.FREE),
      }
    : {}),
  ...(vega_song
    ? {
        vegaSong: transformSong(vega_song, SongType.AI_GENERATED),
      }
    : {}),

  /**
   * Currently mocked. Needs either backend or be handled somewhere else
   */
  effectsOrder: DEFAULT_EFFECTS_ORDER,
});

export type StudioProjectsResponseType = {
  studioProjects: TranformedRemixerProjectResponse[];
  isLastPage: boolean;
};

export const transformProjectsResponse = ({
  items,
  pagination_data,
}: RemixerProjectsListResponse): StudioProjectsResponseType => ({
  studioProjects: items.map(transformProjectResponse),
  isLastPage: pagination_data.total_pages === pagination_data.current_page,
});
